import { useContext, useMemo } from 'react'

import { TaskDefinition } from '@/feature/execution-board'
import { MapMarker } from '@/feature/map'
import { MapContext } from '@/feature/map/context/map'
import { usePersistentObservatoryPageStore } from '@/feature/observatory/store/usePersistentObservatoryPageStore'
import { TASK_MARKER } from '@/feature/observatory/utils/marker-constants'

import { TaskStartMarkerIcon } from '@/feature/observatory/components/markers/TaskMarker'
import { Tooltip } from '@/lib/core/components/Tooltip/Tooltip'

import { getTaskMarkers, TaskMarker } from '../TaskMapOverlay/getTaskMarkers'
import styles from './TaskStartLayer.module.css'

type TaskStartGroup = {
  key: string
  position: google.maps.LatLngLiteral
  start: TaskMarker
  tasks: TaskDefinition[]
}

// Tasks sharing a start location within ~1m are stacked under one marker (e.g. a depot
// dispatching many tasks). 5 decimal places of lat/lng ≈ 1.1m.
const groupKey = (position: google.maps.LatLngLiteral) =>
  `${position.lat.toFixed(5)},${position.lng.toFixed(5)}`

/**
 * Renders the collapsed start marker for every task in a single layer, grouped by start
 * coordinate so overlapping starts remain individually selectable. The focused task is
 * excluded — its full route is drawn by TaskMapOverlay instead.
 */
export const TaskStartLayer = ({ tasks }: { tasks: TaskDefinition[] }) => {
  const map = useContext(MapContext)
  const focusedTaskId = usePersistentObservatoryPageStore((state) => state.focusedTaskId)
  const setFocusedTaskId = usePersistentObservatoryPageStore((state) => state.setFocusedTaskId)
  const setSelectedItem = usePersistentObservatoryPageStore(
    ({ setSelectedItem }) => setSelectedItem,
  )
  const setHideGeoFences = usePersistentObservatoryPageStore(
    ({ asides }) => asides.setHideGeoFences,
  )

  const groups = useMemo(() => {
    const byCoord = new Map<string, TaskStartGroup>()

    for (const task of tasks) {
      if (task.id === focusedTaskId) {
        continue
      }
      const start = getTaskMarkers(task)[0]
      if (!start) {
        continue
      }
      const key = groupKey(start.position)
      const existing = byCoord.get(key)
      if (existing) {
        existing.tasks.push(task)
      } else {
        byCoord.set(key, { key, position: start.position, start, tasks: [task] })
      }
    }

    return [...byCoord.values()]
  }, [tasks, focusedTaskId])

  return (
    <>
      {groups.map((group) => {
        const isMulti = group.tasks.length > 1
        const hasGeofence =
          group.start.locationType === 'GEOFENCE' && group.start.locationObjectId != null

        // Mirror the geofence marker's own click: reveal the geofence aside (it may be hidden),
        // select the geofence so the aside highlights it, then pan the map to its location.
        // Without unhiding + panning, selecting alone is a no-op when the aside is collapsed.
        const viewGeofence = () => {
          setHideGeoFences(false)
          setSelectedItem({ id: group.start.locationObjectId as number, type: 'geoFence' })
          setTimeout(() => {
            map?.panTo({ position: group.position })
          })
        }

        const tooltipContent = (
          <div className={styles.tooltipContent}>
            {isMulti ? (
              <>
                <div className={styles.tooltipHeader}>Tasks</div>
                {group.tasks.map((task) => (
                  <button
                    key={task.id}
                    type="button"
                    className={styles.tooltipButton}
                    onClick={() => setFocusedTaskId(task.id)}
                  >
                    {task.taskDefinition.reference || 'View Task'}
                  </button>
                ))}
              </>
            ) : (
              <button
                type="button"
                className={styles.tooltipButton}
                onClick={() => setFocusedTaskId(group.tasks[0].id)}
              >
                View Task
              </button>
            )}
            {hasGeofence ? (
              <>
                <div className={styles.tooltipDivider} />
                <button type="button" className={styles.tooltipButton} onClick={viewGeofence}>
                  View Geofence
                </button>
              </>
            ) : null}
          </div>
        )

        return (
          <MapMarker
            key={group.key}
            position={group.position}
            zIndex={TASK_MARKER}
            clusterer={false}
          >
            <Tooltip placement="top" trigger="mouseenter" tooltipContent={tooltipContent}>
              <button
                type="button"
                className={styles.marker}
                data-testid="task-start-marker"
                data-task-count={group.tasks.length}
              >
                <TaskStartMarkerIcon />
                {isMulti ? <span className={styles.badge}>{group.tasks.length}</span> : null}
              </button>
            </Tooltip>
          </MapMarker>
        )
      })}
    </>
  )
}
